import { parseUrl } from 'shared/util/gif-parse'
import { FPS } from '../constants'
import { CreationToolFrame, CreationToolSourceFile } from '../types'
import CreationToolFileType from '../types/file-type'
import getVideoFrames from './get-video-frames'

const preloadImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = document.createElement('img')
        img.onload = () => {
            resolve(img)
        }
        img.onerror = error => {
            reject(error)
        }
        img.src = url
    })

const drawToCanvas = (img: HTMLImageElement | HTMLCanvasElement): HTMLCanvasElement => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    canvas.width = img.width
    canvas.height = img.height
    context?.drawImage(img, 0, 0)
    return canvas
}

/**
 * Processes any file or url and returns the file's canvas frames
 * @param source
 */
const getSourceFrames = async (
    sourceFile: CreationToolSourceFile,
    length: number = 0
): Promise<CreationToolFrame[]> => {
    const { source, type, frames } = sourceFile

    // already has frames
    if (frames.length > 0) {
        const totalFrames = length || frames.length
        const pickedFrames: CreationToolFrame[] = []
        for (let i = 0; i < totalFrames; i++) {
            const frame = Math.round((i / totalFrames) * frames.length)
            pickedFrames.push(frames[frame])
        }
        return pickedFrames
    }

    // gif
    if (type === CreationToolFileType.ANIMATED_IMAGE) {
        const fileUrl = typeof source === 'string' ? source : URL.createObjectURL(source)
        const canvases: HTMLCanvasElement[] = await parseUrl(fileUrl, length, FPS)
        if (typeof source !== 'string') {
            URL.revokeObjectURL(fileUrl)
        }
        return canvases.map(canvas => ({ canvas }))
    }

    // video
    if (type === CreationToolFileType.VIDEO) {
        const frames = await getVideoFrames(source, length, FPS)
        return frames
    }

    // still
    if (type === CreationToolFileType.STILL_IMAGE) {
        const fileUrl = typeof source === 'string' ? source : URL.createObjectURL(source)
        const img = await preloadImage(fileUrl || (source as string))
        const canvas = drawToCanvas(img)
        if (typeof source !== 'string') {
            URL.revokeObjectURL(fileUrl)
        }
        return [{ canvas }]
    }

    return []
}

export default getSourceFrames
